import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  k8sListDeployments,
  k8sListNamespaces,
  k8sListPods,
  k8sPodLogs,
  k8sRestartPod,
  k8sRolloutRestart,
  k8sScaleDeployment,
  k8sTopPods,
} from "../../redux/actionCreator";
import { Compass, RotateCw, Square, Play, FileText, ArrowUpDown } from "lucide-react";
import "../Docker/dockerPanel.scss";

const TOP_POLL_MS = 5000;

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KiB", "MiB", "GiB", "TiB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
}
function fmtCpu(milli) {
  if (!milli) return "—";
  if (milli >= 1000) return `${(milli / 1000).toFixed(2)} cores`;
  return `${milli}m`;
}

const PHASE_BADGE = {
  Running: "success",
  Pending: "warning",
  Succeeded: "secondary",
  Failed: "danger",
  Unknown: "secondary",
};

export default function K8sPanel({ show, onHide, project, server }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [namespaces, setNamespaces] = useState([]);
  const [namespace, setNamespace] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [logs, setLogs] = useState(null); // { pod, ns, container, tail, log, loading }
  const [view, setView] = useState("pods"); // pods | deployments
  const [deployments, setDeployments] = useState([]);
  const [busyDep, setBusyDep] = useState(null); // deployment name being acted on

  // Live metrics — keyed by "ns/name" → {cpu_milli, mem_bytes, cpu_raw, mem_raw}
  const [topMap, setTopMap] = useState({});
  const [topAvailable, setTopAvailable] = useState(true);
  const [topMessage, setTopMessage] = useState("");
  const pollRef = useRef(null);

  const projectId = project?._id;
  const serverId = server?._id;

  const load = async (ns = namespace) => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(k8sListPods(projectId, serverId, ns));
    setLoading(false);
    if (res?.status) setRows(res.data || []);
    else {
      setRows([]);
      setErr(res?.message || "Failed to list pods");
    }
  };

  const loadNamespaces = async () => {
    const res = await dispatch(k8sListNamespaces(projectId, serverId));
    if (res?.status) setNamespaces(res.data || []);
  };

  const loadDeployments = async (ns = namespace) => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(k8sListDeployments(projectId, serverId, ns));
    setLoading(false);
    if (res?.status) setDeployments(res.data || []);
    else { setDeployments([]); setErr(res?.message || "Failed to list deployments"); }
  };

  const restartDeployment = (name, ns) => {
    setBusyDep(name);
    dispatch(k8sRolloutRestart(projectId, serverId, name, ns, () => {
      setBusyDep(null);
      loadDeployments(namespace);
    }));
  };

  const scaleDeployment = (name, ns, current) => {
    const v = window.prompt(`Scale ${name} to how many replicas?`, String(current ?? 1));
    if (v === null) return;
    const n = parseInt(v, 10);
    if (!Number.isInteger(n) || n < 0) { alert("Enter a non-negative integer"); return; }
    setBusyDep(name);
    dispatch(k8sScaleDeployment(projectId, serverId, name, n, ns, () => {
      setBusyDep(null);
      loadDeployments(namespace);
    }));
  };

  // Stop = scale to 0 (keeps the deployment object so it's easy to start
  // again). Start = scale back to 1.
  const stopDeployment = (name, ns) => {
    if (!window.confirm(`Stop ${name}? This scales it to 0 replicas (pods terminate; the deployment stays).`)) return;
    setBusyDep(name);
    dispatch(k8sScaleDeployment(projectId, serverId, name, 0, ns, () => {
      setBusyDep(null);
      loadDeployments(namespace);
    }));
  };
  const startDeployment = (name, ns) => {
    setBusyDep(name);
    dispatch(k8sScaleDeployment(projectId, serverId, name, 1, ns, () => {
      setBusyDep(null);
      loadDeployments(namespace);
    }));
  };

  const refreshTop = async (ns = namespace) => {
    if (!projectId || !serverId) return;
    const res = await dispatch(k8sTopPods(projectId, serverId, ns));
    if (!res) return;
    setTopAvailable(!!res.available);
    setTopMessage(res.available ? "" : (res.message || ""));
    const map = {};
    for (const m of res.data || []) {
      // For single-namespace queries the backend leaves m.namespace empty;
      // fall back to the queried ns so the key matches the pod row.
      const k = `${m.namespace || ns}/${m.name}`;
      map[k] = m;
    }
    setTopMap(map);
  };

  useEffect(() => {
    if (show) {
      const defaultNs = server?.k8s_namespace || "";
      setNamespace(defaultNs);
      loadNamespaces();
      load(defaultNs);
      refreshTop(defaultNs);
      // Poll while the modal is open.
      pollRef.current = setInterval(() => refreshTop(defaultNs), TOP_POLL_MS);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      };
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, projectId, serverId]);

  // Re-arm the poller against the latest namespace whenever it changes.
  useEffect(() => {
    if (!show) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => refreshTop(namespace), TOP_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, show]);

  const restart = (row) => {
    if (
      !window.confirm(
        `Delete pod ${row.namespace}/${row.name}? If managed by a Deployment/StatefulSet, it'll be recreated.`
      )
    )
      return;
    dispatch(
      k8sRestartPod(projectId, serverId, row.name, row.namespace, (ok) =>
        ok && load(namespace)
      )
    );
  };

  const openLogs = async (row, container) => {
    const c = container || (row.containers && row.containers[0]) || "";
    setLogs({ pod: row.name, ns: row.namespace, container: c, tail: 500, log: "", loading: true });
    const res = await dispatch(
      k8sPodLogs(projectId, serverId, row.name, {
        namespace: row.namespace,
        container: c,
        tail: 500,
      })
    );
    if (res?.status) {
      setLogs({ pod: row.name, ns: row.namespace, container: c, tail: 500, log: res.data?.log || "", loading: false });
    } else {
      setLogs({ pod: row.name, ns: row.namespace, container: c, tail: 500, log: res?.message || "(no logs)", loading: false });
    }
  };

  const openTerminal = (row, container) => {
    const c = container || (row.containers && row.containers[0]) || "";
    const qs = new URLSearchParams({
      label: `${server.label} · ${row.namespace}/${row.name}${c ? " · " + c : ""}`,
      host: `${server.username}@${server.host}:${server.port}`,
      pod: row.name,
      namespace: row.namespace,
      ...(c ? { container: c } : {}),
    });
    window.open(
      `/terminal/${projectId}/${serverId}?${qs.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="m-0">Kubernetes</h4>
            <span className="text-muted">{server?.label}</span>
            <code className="small">
              {server?.username}@{server?.host}:{server?.port}
            </code>
          </div>
        </Modal.Header>
        <Modal.Body className="docker-panel-body">
          <div className="docker-toolbar">
            <Form.Select
              size="sm"
              value={namespace}
              onChange={(e) => {
                const v = e.target.value;
                setNamespace(v);
                load(v);
                refreshTop(v);
              }}
              style={{ maxWidth: 240 }}
            >
              <option value="">— all namespaces / kubectl default —</option>
              {namespaces.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
            <div className="btn-group btn-group-sm" role="group">
              <Button size="sm" variant={view === "pods" ? "primary" : "light"}
                onClick={() => { setView("pods"); load(namespace); }}>Pods</Button>
              <Button size="sm" variant={view === "deployments" ? "primary" : "light"}
                onClick={() => { setView("deployments"); loadDeployments(namespace); }}>Deployments</Button>
            </div>
            <Button size="sm" variant="light" onClick={() => view === "pods" ? load(namespace) : loadDeployments(namespace)}>
              Refresh
            </Button>
            {!topAvailable && topMessage && view === "pods" && (
              <span
                className="badge bg-warning text-dark"
                title="Live CPU/Memory needs metrics-server installed on the cluster"
              >
                no metrics: {topMessage}
              </span>
            )}
            <span className="text-muted ms-auto small">
              {view === "pods"
                ? `${rows.length} pod${rows.length === 1 ? "" : "s"}${topAvailable ? " · live" : ""}`
                : `${deployments.length} deployment${deployments.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {err && <div className="alert alert-danger m-3">{err}</div>}

          {view === "deployments" && (
            <div className="docker-table-wrap">
              <table className="docker-table">
                <thead>
                  <tr>
                    <th>Deployment</th>
                    <th>Ready</th>
                    <th>Image(s)</th>
                    <th className="text-end" style={{ width: 1 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="empty"><Spinner size="sm" animation="border" /> Loading…</td></tr>
                  ) : deployments.length === 0 ? (
                    <tr><td colSpan={4} className="empty">No deployments in this namespace.</td></tr>
                  ) : (
                    deployments.map((d) => {
                      const healthy = d.ready === d.desired && d.desired > 0;
                      return (
                        <tr key={`${d.namespace}/${d.name}`}>
                          <td>
                            <div className="emp-name">{d.name}</div>
                            <div className="emp-id">{d.namespace}</div>
                          </td>
                          <td>
                            <span className={`badge bg-${healthy ? "success" : d.ready > 0 ? "warning" : "secondary"}`}>
                              {d.ready}/{d.desired}
                            </span>
                          </td>
                          <td><code className="small">{(d.images || []).join(", ")}</code></td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-warning" className="me-1"
                              disabled={busyDep === d.name}
                              onClick={() => restartDeployment(d.name, d.namespace)}
                              title="kubectl rollout restart">
                              {busyDep === d.name ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><RotateCw size={12} /> Restart</span>}
                            </Button>
                            <Button size="sm" variant="light" className="me-1"
                              disabled={busyDep === d.name}
                              onClick={() => scaleDeployment(d.name, d.namespace, d.desired)}
                              title="Scale replicas">
                              <span className="d-inline-flex align-items-center gap-1"><ArrowUpDown size={12} /> Scale</span>
                            </Button>
                            {d.desired > 0 ? (
                              <Button size="sm" variant="outline-danger"
                                disabled={busyDep === d.name}
                                onClick={() => stopDeployment(d.name, d.namespace)}
                                title="Scale to 0 (stop)">
                                <Square size={12} fill="currentColor" className="me-1" /> Stop
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline-success"
                                disabled={busyDep === d.name}
                                onClick={() => startDeployment(d.name, d.namespace)}
                                title="Scale back to 1 (start)">
                                <Play size={12} className="me-1" /> Start
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {view === "pods" && (
          <div className="docker-table-wrap">
            <table className="docker-table">
              <thead>
                <tr>
                  <th>Pod</th>
                  <th>Ready</th>
                  <th>Phase</th>
                  <th>CPU</th>
                  <th>Memory</th>
                  <th>Restarts</th>
                  <th>Node</th>
                  <th>IP</th>
                  <th className="text-end" style={{ width: 1 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      <Spinner size="sm" animation="border" /> Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      <div className="empty-state">
                        <div className="empty-art"><Compass size={32} strokeWidth={1.2} /></div>
                        <h4>No pods</h4>
                        <p>
                          Either kubectl can't reach a cluster, the namespace is empty,
                          or the SSH user lacks permission.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const metric = topMap[`${r.namespace}/${r.name}`];
                    return (
                    <tr key={`${r.namespace}/${r.name}`}>
                      <td>
                        <div className="emp-name">{r.name}</div>
                        <div className="emp-id">{r.namespace}</div>
                      </td>
                      <td>{r.ready}</td>
                      <td>
                        <span className={`badge bg-${PHASE_BADGE[r.phase] || "secondary"}`}>
                          {r.phase || "—"}
                        </span>
                        {r.reason && (
                          <div
                            className="badge bg-danger mt-1"
                            style={{ whiteSpace: "normal", textAlign: "left", maxWidth: 220 }}
                            title={r.reason_message || r.reason}
                          >
                            {r.reason}
                          </div>
                        )}
                      </td>
                      <td className="small">
                        <code>{metric ? fmtCpu(metric.cpu_milli) : "—"}</code>
                      </td>
                      <td className="small">
                        <code>{metric ? fmtBytes(metric.mem_bytes) : "—"}</code>
                      </td>
                      <td>{r.restarts}</td>
                      <td className="small">{r.node || "—"}</td>
                      <td className="small">
                        <code>{r.podIp || "—"}</code>
                      </td>
                      <td className="text-end">
                        <Dropdown align="end" className="d-inline">
                          <Dropdown.Toggle
                            size="sm"
                            variant="light"
                            className="row-actions-btn"
                          >
                            ⋯
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {(r.containers || []).length <= 1 ? (
                              <>
                                <Dropdown.Item onClick={() => openLogs(r)}>
                                  <FileText size={12} className="me-1" /> Logs
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openTerminal(r)}
                                  disabled={r.phase !== "Running"}
                                >
                                  <Play size={12} className="me-1" /> Terminal (inside)
                                </Dropdown.Item>
                              </>
                            ) : (
                              <>
                                <Dropdown.Header>Logs</Dropdown.Header>
                                {(r.containers || []).map((c) => (
                                  <Dropdown.Item
                                    key={`logs-${c}`}
                                    onClick={() => openLogs(r, c)}
                                  >
                                    <FileText size={12} className="me-1" /> {c}
                                  </Dropdown.Item>
                                ))}
                                <Dropdown.Header>Terminal</Dropdown.Header>
                                {(r.containers || []).map((c) => (
                                  <Dropdown.Item
                                    key={`term-${c}`}
                                    onClick={() => openTerminal(r, c)}
                                    disabled={r.phase !== "Running"}
                                  >
                                    <Play size={12} className="me-1" /> {c}
                                  </Dropdown.Item>
                                ))}
                              </>
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => restart(r)}
                              className="text-warning"
                            >
                              <RotateCw size={12} className="me-1" /> Restart (delete pod)
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {logs && (
        <Modal show onHide={() => setLogs(null)} size="xl" centered>
          <Modal.Header closeButton>
            <h5 className="m-0">
              Logs — <code>{logs.ns}/{logs.pod}</code>
              {logs.container && <span className="text-muted ms-2 small">container: <code>{logs.container}</code></span>}{" "}
              <small className="text-muted">last {logs.tail} lines</small>
            </h5>
          </Modal.Header>
          <Modal.Body className="p-0">
            {logs.loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <pre className="docker-logs">{logs.log || "(empty)"}</pre>
            )}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}
